import classNames from 'classnames';
import {getMediaSrc} from 'core/helpers/media';
import {IMedia} from 'modules/products/types';
import React, {FC, useCallback, useEffect, useId, useMemo, useState} from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import style from './style.module.scss';
import Picture from 'components/Picture';
import CustomRoutingLink from 'components/CustomRoutingLink';
import {IProductSnippet} from 'shared/types/productSnippet.types';
import {Swiper, SwiperClass, SwiperSlide} from 'swiper/react';
import {Pagination} from 'swiper/modules';
import {useIsSkeleton} from 'modules/skeleton/hooks/useIsSkeleton';
import {NoImageNew} from 'components/NoImageNew';

type TGalleryProps = {
    className?: string;
    imageList: IMedia['images'];
    url: string;
    onActiveImageChange?: (index: number) => void;
} & Pick<IProductSnippet['analytics'], 'onProductClick'> &
    Pick<IProductSnippet, 'availability'>;

const MAX_VISIBLE_SLIDES = 3;

export const Gallery: FC<TGalleryProps> = ({
    className,
    imageList,
    url,
    onProductClick,
    availability,
    onActiveImageChange,
}) => {
    const shortImagesList = useMemo(() => imageList?.slice(0, MAX_VISIBLE_SLIDES), [imageList]);
    const [activeItemIndex, setActiveItemIndex] = useState<number>(0);
    const isSkeleton = useIsSkeleton();
    const uid = useId();

    useEffect(() => {
        onActiveImageChange?.(activeItemIndex);
    }, [activeItemIndex, onActiveImageChange]);

    const handleBulletHover = useCallback((index: number) => {
        return () => setActiveItemIndex(index);
    }, []);

    const handleBulletLeave = useCallback(() => {
        setActiveItemIndex(0);
    }, []);

    const handleSlideChange = useCallback((swiper: SwiperClass) => {
        setActiveItemIndex(swiper.realIndex);
    }, []);

    const paginationParams = useMemo(
        () => ({
            clickable: true,
            dynamicBullets: false,
        }),
        [],
    );

    const isOutOfStock = useMemo(() => 'OUT_OF_STOCK' === availability, [availability]);

    return (
        <CustomRoutingLink href={url} className={style.root} onClick={onProductClick}>
            {shortImagesList?.length ? (
                <div
                    data-skeleton-item={true}
                    className={classNames(className, style.productImageSwitcher, {
                        [style.isOutOfStock]: isOutOfStock,
                    })}
                >
                    <Swiper
                        enabled={!isSkeleton}
                        className={style.slider}
                        modules={[Pagination]}
                        pagination={paginationParams}
                        loop={true}
                        slidesPerView={1}
                        spaceBetween={0}
                        direction={'horizontal'}
                        touchMoveStopPropagation={true}
                        nested={true}
                        onSlideChange={handleSlideChange}
                    >
                        {shortImagesList.map((image, index) => (
                            <SwiperSlide
                                data-skeleton-item={''}
                                className={style.productImageSwitcherImageSlide}
                                key={`${image}-${index}`}
                            >
                                <Picture
                                    key={`${uid}${image}${index}`}
                                    src={getMediaSrc(image, {height: 300, isWebp: true})}
                                    alt={'Изображение товара'}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <div
                        className={style.productImageSwitcherImageContainer}
                        style={{transform: `translateX(${activeItemIndex * -100}%)`}}
                    >
                        {shortImagesList.map((image, index) => (
                            <div className={style.productImageSwitcherImageSlide} key={`${image}-${index}`}>
                                <Picture
                                    key={`${uid}${image}${index}`}
                                    src={getMediaSrc(image, {width: 300, isWebp: true})}
                                    alt={'Изображение товара'}
                                />
                            </div>
                        ))}
                    </div>

                    {!isOutOfStock && (
                        <div className={style.productImageSwitcherPaginationContainer}>
                            {shortImagesList.map((image, index) => (
                                <div
                                    className={style.productImageSwitcherPagination}
                                    key={`${image}${index}`}
                                    onMouseEnter={handleBulletHover(index)}
                                    onMouseLeave={handleBulletLeave}
                                />
                            ))}
                        </div>
                    )}

                    {1 < shortImagesList.length && !isOutOfStock && (
                        <ul className={classNames(style.productImageSwitcherNavItems, style.productImageSwitcherNav)}>
                            {shortImagesList.map((image, index) => (
                                <li
                                    className={classNames(style.productImageSwitcherNavItem, {
                                        [style.productImageSwitcherNavItemActive]: activeItemIndex === index,
                                    })}
                                    key={`${image}${index}`}
                                    onMouseEnter={handleBulletHover(index)}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            ) : (
                <NoImageNew />
            )}
        </CustomRoutingLink>
    );
};
